import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";

import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import {
  CrearVersionPeticion,
  CrearVersionRespuesta,
  DetalleVersion,
  EditarVersionPeticion,
  EditarVersionRespuesta,
  EjecucionPruebaPeticion,
  EjecucionPruebaRespuesta,
  EtapasVersionesRespuesta,
  ResumenEtapasRespuesta,
} from "./etapas-versiones.model";
import {
  ConsultaIncidenciasEtapa,
  IncidenciaNueva,
  IncidenciasDeLaConsulta,
  RespuestaAltaIncidencia,
  consultaIncidenciasDeEtapa,
  incidenciasDeLaConsulta,
} from "./incidencias-prueba.util";

/**
 * Etapas, versiones y pruebas de Gestión de Proyectos (plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §6.1). Cada fase
 * agrega aquí sus endpoints: en la 1B, la lectura de A1/T1 y crear versión;
 * en la 2, la página del proyecto y el resumen de etapas de las tarjetas; en
 * la 4, A2, editar versión y las ejecuciones de pruebas (T2) con su
 * evidencia, más las dos llamadas de incidencias que usa T2 (§6.3).
 *
 * El tenant, la hoja de Gestión de Proyectos y D4/D8 los decide el servidor
 * con la sesión; los rechazos llegan con 200 en pCodRetorno/pMensaje, y sin
 * sesión con 401 (el ErrorInterceptor cierra la sesión). La descarga de
 * evidencia responde los bytes, así que sus rechazos van en el estado HTTP.
 */
@Injectable({ providedIn: "root" })
export class EtapasVersionesService {
  constructor(private readonly http: HttpClient) {}

  /** PROYECTOS_ETAPAS_VERSIONES: las etapas del proyecto con su estado, indicadores y versiones. */
  etapasVersiones(idProyecto: string): Observable<EtapasVersionesRespuesta> {
    return this.http.post<EtapasVersionesRespuesta>(UrlConstantes.PROYECTOS_ETAPAS_VERSIONES, { idProyecto });
  }

  /**
   * PROYECTOS_PAGINA: el proyecto, con la misma decoración del servidor que
   * un elemento de la lista (riesgos por severidad, horas reales y puestos).
   * 00003 si no existe, es de otro tenant o la lista no lo mostraría.
   */
  pagina(idProyecto: string): Observable<Proyecto> {
    return this.http.post<Proyecto>(UrlConstantes.PROYECTOS_PAGINA, { idProyecto });
  }

  /** PROYECTOS_ETAPAS_RESUMEN: las etapas y los defectos de hasta 50 proyectos en una llamada. */
  resumenEtapas(idsProyecto: string[]): Observable<ResumenEtapasRespuesta> {
    return this.http.post<ResumenEtapasRespuesta>(UrlConstantes.PROYECTOS_ETAPAS_RESUMEN, { idsProyecto });
  }

  /** PROYECTOS_VERSION_CREAR: crea la versión (idempotente por el id que manda el diálogo). */
  crearVersion(peticion: CrearVersionPeticion): Observable<CrearVersionRespuesta> {
    return this.http.post<CrearVersionRespuesta>(UrlConstantes.PROYECTOS_VERSION_CREAR, peticion);
  }

  /** PROYECTOS_VERSION_EDITAR: nombre (único en la etapa), ruta y descripción de una versión (§5.5). */
  editarVersion(peticion: EditarVersionPeticion): Observable<EditarVersionRespuesta> {
    return this.http.post<EditarVersionRespuesta>(UrlConstantes.PROYECTOS_VERSION_EDITAR, peticion);
  }

  /** PROYECTOS_VERSION_DETALLE: la página de una versión (A2). */
  detalleVersion(idProyecto: string, idVersion: string): Observable<DetalleVersion> {
    return this.http.post<DetalleVersion>(UrlConstantes.PROYECTOS_VERSION_DETALLE, { idProyecto, idVersion });
  }

  /**
   * PROYECTOS_PRUEBAS_GUARDAR (T2): multipart con la parte «ejecucion» como
   * JSON (un Blob application/json, como el comentario con archivos de
   * oportunidades) y los «archivos» de evidencia nuevos.
   */
  guardarEjecucion(peticion: EjecucionPruebaPeticion, archivos: readonly File[]): Observable<EjecucionPruebaRespuesta> {
    const datos = new FormData();
    datos.append("ejecucion", new Blob([JSON.stringify(peticion)], { type: "application/json" }));
    archivos.forEach((archivo) => datos.append("archivos", archivo, archivo.name));
    return this.http.post<EjecucionPruebaRespuesta>(UrlConstantes.PROYECTOS_PRUEBAS_GUARDAR, datos);
  }

  /**
   * PROYECTOS_PRUEBAS_EVIDENCIA: el archivo como blob, con el interceptor
   * normal para que viaje el token. 400, 403, 404 o 500 sin cuerpo.
   */
  evidencia(idEjecucion: string, archivo: string): Observable<HttpResponse<Blob>> {
    return this.http.post(
      UrlConstantes.PROYECTOS_PRUEBAS_EVIDENCIA,
      { idEjecucion, archivo },
      { observe: "response", responseType: "blob" },
    );
  }

  /**
   * Las incidencias de la etapa para «Vincular incidencia existente» (T2):
   * CONSINCXFILTROQA, el mismo filtro del tablero de QA (proyecto, etapa y el
   * tenant de la sesión), con todos los estatus.
   */
  incidenciasDeLaEtapa(idProyecto: string, idEtapa: string): Observable<IncidenciasDeLaConsulta> {
    return this.http
      .post<ConsultaIncidenciasEtapa>(UrlConstantes.CONSINCXFILTROQA, consultaIncidenciasDeEtapa(idProyecto, idEtapa))
      .pipe(map((respuesta) => incidenciasDeLaConsulta(respuesta)));
  }

  /**
   * «Crear incidencia» de T2: guardarIncidenciaQA.do, el alta de siempre
   * (nace en ABIERTO con la versión que se está probando). En un proyecto de
   * Mongo rechaza en el cuerpo (VAL-INC-VERSION) una versión que no sea de la
   * etapa.
   */
  crearIncidencia(incidencia: IncidenciaNueva): Observable<RespuestaAltaIncidencia> {
    return this.http.post<RespuestaAltaIncidencia>(UrlConstantes.guardaIncidencia, incidencia);
  }
}
